/**
 * pokemonArtwork — runtime colour-artwork source for CAUGHT dex cells.
 *
 * Build 7 (completion payoff): a caught Pokémon should reward the trainer
 * with full colour art instead of the Style B silhouette it shared with the
 * uncaught state. The trainer's own observed crop (localSpriteStore) is still
 * preferred when present — this module only provides the fallback colour art
 * for caught species the user hasn't personally cropped yet.
 *
 * Source priority (per Build 7 brief):
 *   1. observed crop (handled by the caller via localSpriteStore)
 *   2. official-artwork  (this module — primary)
 *   3. HOME render       (this module — onError fallback)
 *
 * Both are served from our own mirror (sprites.nexusdex.ai) — see spriteCdn.ts.
 * They are sourced from PokeAPI at build time via
 * `scripts/sync-sprites-to-r2.ts`, never fetched from PokeAPI at runtime.
 *
 * NB: silhouettes remain the source of identity for UNCAUGHT cells. Note that
 * PriorityVariantDex does render colour art for missing cells too (see
 * prewarmSprites.ts), so this is not strictly caught-only.
 */
import { SPRITE_CDN_POKEAPI_OTHER } from "@/lib/spriteCdn";

/**
 * IN-APP BUNDLED colour art (downscaled WebP shipped in the binary).
 *
 * Why: on first launch, before the on-device precache (spritePrecache) has run,
 * caught + priority-grid colour art would otherwise stream from the CDN one by
 * one, so a fresh install shows a half-blank grid on a cold/slow network — a
 * first-impression the "download then delete" churn points at. We ship a
 * downscaled WebP of every base + shiny species in `client/public/sprites-
 * bundled/` so this art is available OFFLINE from the very first paint. Only the
 * strays (forms, brand-new releases not yet in the bundle) fall through to the
 * CDN. Preferred BELOW the device precache (which stores full-res), so once the
 * precache fills in, the higher-res copy wins; the bundle is the first-launch
 * stopgap. A missing file 404s and CaughtSprite falls through to the CDN, so the
 * wiring is inert-and-safe until `npm run sprites:bundle` has populated the dir.
 *
 * Served from the app's own origin via Vite's public dir, so it is same-origin
 * and needs no network. `import.meta.env.BASE_URL` keeps it correct under any
 * configured base path (Capacitor serves the bundle at root).
 */
export function bundledArtworkUrl(id: number, opts?: { shiny?: boolean }): string {
  const variant = opts?.shiny ? "shiny" : "base";
  return `${import.meta.env.BASE_URL}sprites-bundled/${variant}/${id}.webp`;
}

/** Primary colour art: official-artwork PNG for a national-dex id. */
export function officialArtworkUrl(id: number, opts?: { shiny?: boolean }): string {
  const variant = opts?.shiny ? "official-artwork/shiny" : "official-artwork";
  return `${SPRITE_CDN_POKEAPI_OTHER}/${variant}/${id}.png`;
}

/**
 * Species whose FEMALE looks different and has its own HOME render on the
 * sprite mirror (checked 2026-09-20: other/home/female/<id>.png answers
 * 200 for these three; Meowstic, Indeedee, Basculegion, Oinkologne,
 * Unfezant and Hippowdon do not, their females being separate form ids).
 * A female Litleo's destiny is the maneless Pyroar, not the maned male.
 */
export const FEMALE_HOME_ART: ReadonlySet<number> = new Set([668, 592, 593]);

/**
 * The art for a destiny target: official artwork, or the female HOME
 * render when the record is female and the species has one.
 */
export function destinyArtworkUrl(id: number, opts?: { shiny?: boolean; gender?: "male" | "female" | null }): string {
  if (opts?.gender === "female" && FEMALE_HOME_ART.has(id)) {
    const variant = opts.shiny ? "home/shiny/female" : "home/female";
    return `${SPRITE_CDN_POKEAPI_OTHER}/${variant}/${id}.png`;
  }
  return officialArtworkUrl(id, { shiny: opts?.shiny });
}

/** Fallback colour art: HOME render, used when official-artwork 404s. */
export function homeArtworkUrl(id: number, opts?: { shiny?: boolean }): string {
  const variant = opts?.shiny ? "home/shiny" : "home";
  return `${SPRITE_CDN_POKEAPI_OTHER}/${variant}/${id}.png`;
}
